import { BadgeStatus } from '@/components/badge-order';
import Pagination from '@/components/commons/pagination';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useModal from '@/hooks/useModal';
import { cn, formatIDR } from '@/lib/utils';
import { format } from 'date-fns';

type OrderDetal = {
  order_id: string;
  order_no: string;
  grand_total: number;
  status: string;
  created_at: string;
};

type TableCustomerOrderMobileProps = {
  data: OrderDetal[];
  totalCount: number;
  isLoading: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  drawer: ReturnType<typeof useModal>;
};

export default function TableCustomerOrderMobile({
  data,
  totalCount,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
  drawer,
}: TableCustomerOrderMobileProps) {
  if (isLoading) {
    return (
      <>
        <SkeletonLoading />
        <div className='mt-10'>
          <Skeleton className='h-10 w-full' />
        </div>
      </>
    );
  }

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <div className='py-10 text-center text-gray-500'>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <>
      <div className='space-y-2'>
        {data.map((item, index) => (
          <div
            key={index}
            className={cn('flex w-full flex-col rounded-lg border p-4 px-3 text-sm')}
          >
            <div className='flex justify-between'>
              <Button
                type='button'
                variant='link'
                className='m-0 p-0'
                onClick={() => drawer.open({ order_id: item.order_id })}
              >
                #{item.order_no}
              </Button>
              {formatIDR(item.grand_total)}
            </div>
            <div className='flex justify-between'>
              <span>{format(item.created_at, 'd MMM yyyy')}</span>
              <BadgeStatus status={item.status.toLowerCase()} />
            </div>
          </div>
        ))}
      </div>
      <div className='mt-10'>
        <Pagination
          isShowPageSize={false}
          currentPage={pagination.pageIndex + 1}
          totalCount={totalCount}
          totalSiblingCount={3}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </>
  );
}

const SkeletonLoading = () => (
  <div className='space-y-4'>
    {[...Array(5)].map((_, idx) => (
      <div key={idx} className='flex justify-between border-b border-[#ECEFF1] py-4'>
        <div className='flex items-center'>
          <div>
            <Skeleton className='mb-2 h-4 w-24' />
            <Skeleton className='mb-1 h-5 w-32' />
            <Skeleton className='h-3 w-20' />
          </div>
        </div>
        <div className='text-right'>
          <Skeleton className='mb-2 h-4 w-20' />
          <div className='flex items-center justify-end'>
            <Skeleton className='h-3 w-24' />
          </div>
          <Skeleton className='mt-1 h-5 w-20' />
        </div>
      </div>
    ))}
  </div>
);
